import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getApiUrl, authHeaders } from '@/lib/api';
import { useAuth } from '@/hooks/auth/useAuth';
import type { ImpersonationState } from '@reflex/plugin-impersonation/shared/types';

/**
 * Host-owned read/stop hook for the active impersonation session. Lives in
 * `web/` (not the plugin) so the in-UI escape hatch never depends on
 * `plugin-impersonation`'s web manifest reaching the client — if the
 * `/config/plugins` filter ever strips that manifest mid-session, the
 * CommandPalette fallback still works because it consumes this hook
 * directly.
 *
 * Uses the same React-Query key as the plugin's `useImpersonation` so the
 * two stay in sync: a stop initiated here surfaces in the plugin's banner
 * (when mounted) without an extra round-trip, and vice versa.
 */
const QUERY_KEY = ['impersonation-state'] as const;

async function fetchImpersonationState(): Promise<ImpersonationState | null> {
  const res = await fetch(getApiUrl('/impersonation/state'), {
    headers: authHeaders(),
    credentials: 'include',
  });
  // 401 means no session. The route lives in the user-scoped bucket
  // (no active-org gate), so a 403 here would only come from an
  // unexpected upstream guard; treat it as "not impersonating" rather
  // than throwing so the in-UI escape hatch degrades silently.
  if (res.status === 401 || res.status === 403) return null;
  if (!res.ok) throw new Error(`impersonation state failed: ${res.status}`);
  const body = (await res.json()) as ImpersonationState | null;
  return body;
}

async function stopImpersonation(): Promise<void> {
  const res = await fetch(getApiUrl('/impersonation/stop'), {
    method: 'POST',
    headers: authHeaders(),
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`impersonation stop failed: ${res.status}`);
}

export interface ImpersonationStatusResult {
  state: ImpersonationState | null;
  isActive: boolean;
  stop: () => Promise<void>;
  isStopping: boolean;
}

export function useImpersonationStatus(): ImpersonationStatusResult {
  const queryClient = useQueryClient();
  const { authenticated, refreshSession } = useAuth();

  const stateQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchImpersonationState,
    staleTime: 5_000,
    enabled: authenticated,
  });

  const stopMutation = useMutation({
    mutationFn: stopImpersonation,
    onSuccess: async () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      // The swap touched every org-scoped read, so wipe the cache and let
      // the next render pick up the actor's own data.
      void queryClient.invalidateQueries();
      // `/auth/me` lives outside React Query (it backs `useAuth`'s
      // reducer), so cache invalidation alone leaves the nav stuck on
      // the target's identity after the swap is torn down. Pull a fresh
      // `/auth/me` so memberships / active org / permissions snap back
      // to the actor.
      await refreshSession();
    },
  });

  const state = stateQuery.data ?? null;
  return {
    state,
    isActive: state !== null,
    stop: stopMutation.mutateAsync,
    isStopping: stopMutation.isPending,
  };
}
